import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2, Route } from "lucide-react";
import { routeInfo } from "@/lib/maps";

const TILES = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTRIBUTION = "&copy; OpenStreetMap";

const pin = (label, colour) =>
  L.divIcon({
    className: "",
    html: `<div style="background:${colour};color:#0f131c;width:26px;height:26px;border-radius:50%;
      display:flex;align-items:center;justify-content:center;font:700 12px/1 ui-monospace,monospace;
      box-shadow:0 2px 8px rgba(0,0,0,.5);border:2px solid rgba(0,0,0,.35)">${label}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });

/** Route map drawn with open tiles and a server-calculated line — no browser Maps key needed. */
export const RouteMap = ({ points = [], onRoute, testId = "route-map", vehicleAtStart = false }) => {
  const holder = useRef(null);
  const map = useRef(null);
  const layer = useRef(null);
  const [state, setState] = useState({ status: "idle", miles: null, minutes: null });

  const coords = (points || []).filter((p) => p && p.lat != null && p.lng != null);
  const key = coords.map((p) => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`).join("|");

  useEffect(() => {
    if (!holder.current || map.current) return;
    map.current = L.map(holder.current, { zoomControl: true, attributionControl: true })
      .setView([29.76, -95.37], 10);
    L.tileLayer(TILES, { attribution: ATTRIBUTION, maxZoom: 19 }).addTo(map.current);
    layer.current = L.layerGroup().addTo(map.current);
    return () => { map.current?.remove(); map.current = null; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (coords.length < 2) {
      setState({ status: "idle", miles: null, minutes: null });
      layer.current?.clearLayers();
      return;
    }
    setState((s) => ({ ...s, status: "loading" }));
    (async () => {
      const info = await routeInfo(coords);
      if (cancelled || !map.current) return;
      if (!info) {
        setState({ status: "error", miles: null, minutes: null });
        return;
      }
      const line = info.path?.length ? info.path : coords.map((p) => [p.lat, p.lng]);
      layer.current.clearLayers();
      L.polyline(line, { color: "#d4af37", weight: 4, opacity: 0.9 }).addTo(layer.current);
      const labels = ["A", ...coords.slice(1, -1).map((_, i) => String(i + 1)), "B"];
      coords.forEach((p, i) => {
        const isStart = i === 0;
        L.marker([p.lat, p.lng], {
          icon: pin(isStart && vehicleAtStart ? "\u25B2" : labels[i] || String(i + 1),
                    isStart ? "#d4af37" : "#e5e7eb"),
        }).addTo(layer.current);
      });
      map.current.fitBounds(L.latLngBounds(line), { padding: [28, 28] });
      setState({ status: "ready", miles: info.miles, minutes: info.minutes });
      onRoute?.({ miles: info.miles, minutes: info.minutes });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <div data-testid={testId} className="overflow-hidden rounded-lg border border-ink-600">
      <div className="relative">
        <div ref={holder} className="h-56 w-full bg-ink-900" data-testid={`${testId}-canvas`} />
        {coords.length < 2 && (
          <div className="absolute inset-0 flex items-center justify-center bg-ink-900/85 px-6 text-center">
            <p className="text-xs text-slate-500">
              Pick your pickup and drop-off — your route and drive time appear here.
            </p>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-ink-600 bg-ink-900/70 px-4 py-2.5">
        <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-slate-500">
          <Route className="h-3 w-3 text-gold" /> Your route
        </p>
        {state.status === "loading" && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500" />}
        {state.status === "error" && (
          <span data-testid={`${testId}-error`} className="text-[11px] text-slate-500">
            Route unavailable — pricing still works
          </span>
        )}
        {state.status === "ready" && (
          <span data-testid={`${testId}-summary`} className="font-mono text-[11px] text-slate-300">
            {state.miles} mi · about {state.minutes} min drive
          </span>
        )}
      </div>
    </div>
  );
};
